import { useState } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import { motion, AnimatePresence } from "framer-motion";
import {
  SunDim, Leaf, Lightning, FireSimple, Wind, Drop,
  Moon, ThermometerHot, Heart, Star, Sparkle,
  Palette, Flower, PencilLine, ArrowRight,
} from "@phosphor-icons/react";
import FrequencyPlayer from "../components/FrequencyPlayer";
import SmartNudges from "../components/SmartNudges";
import WhatToWear from "../components/WhatToWear";
import MoodOutfitOfTheDay from "../components/MoodOutfitOfTheDay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOODS = [
  { id: "happy", label: "Happy", color: "#F4A261", Icon: SunDim },
  { id: "calm", label: "Calm", color: "#84A98C", Icon: Leaf },
  { id: "energized", label: "Energized", color: "#E9C46A", Icon: Lightning },
  { id: "stressed", label: "Stressed", color: "#E76F51", Icon: FireSimple },
  { id: "anxious", label: "Anxious", color: "#CDB4DB", Icon: Wind },
  { id: "sad", label: "Sad", color: "#457B9D", Icon: Drop },
  { id: "tired", label: "Tired", color: "#A8DADC", Icon: Moon },
  { id: "frustrated", label: "Frustrated", color: "#E07A5F", Icon: ThermometerHot },
  { id: "grateful", label: "Grateful", color: "#2D6A4F", Icon: Heart },
  { id: "hopeful", label: "Hopeful", color: "#FFD166", Icon: Star },
];

const FEELINGS = [
  "Overwhelmed", "Peaceful", "Restless", "Motivated", "Content",
  "Lonely", "Nervous", "Excited", "Numb", "Scattered",
];

export default function MoodScanPage() {
  const { user } = useAuth();
  const [selectedMood, setSelectedMood] = useState(null);
  const [intensity, setIntensity] = useState([5]);
  const [selectedFeelings, setSelectedFeelings] = useState([]);
  const [note, setNote] = useState("");
  const [formula, setFormula] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1=mood, 2=details, 3=result

  const toggleFeeling = (f) => {
    setSelectedFeelings((prev) =>
      prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]
    );
  };

  const handleScan = async () => {
    if (!selectedMood) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/mood/scan`,
        {
          mood: selectedMood.id,
          intensity: intensity[0],
          feelings: selectedFeelings,
          note: note || null,
        },
        { withCredentials: true }
      );
      setFormula(res.data.formula);
      setStep(3);
    } catch (err) {
      console.error("Mood scan failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const resetScan = () => {
    setSelectedMood(null);
    setIntensity([5]);
    setSelectedFeelings([]);
    setNote("");
    setFormula(null);
    setStep(1);
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="mood-scan-page">
      {/* Header */}
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#2D6A4F] mb-2" data-testid="mood-scan-greeting">
          Welcome, {user?.name?.split(" ")[0] || "friend"}
        </p>
        <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-[#1A2421] tracking-tight">
          How are you feeling?
        </h1>
      </div>

      {/* Mood Outfit of the Day */}
      <MoodOutfitOfTheDay />

      {/* Smart Nudges */}
      <SmartNudges />

      <AnimatePresence mode="wait">
        {/* Step 1: Mood Wheel */}
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {MOODS.map((mood, i) => (
                <motion.button
                  key={mood.id}
                  data-testid={`mood-wheel-${mood.id}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.04 }}
                  whileHover={{ scale: 1.08, y: -4 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setSelectedMood(mood);
                    setStep(2);
                  }}
                  className="flex flex-col items-center gap-2 p-4 rounded-2xl transition-all duration-300 border-2 w-[calc(20%-0.6rem)] min-w-[80px]"
                  style={{
                    backgroundColor: selectedMood?.id === mood.id ? mood.color + "20" : "white",
                    borderColor: selectedMood?.id === mood.id ? mood.color : "transparent",
                    boxShadow: selectedMood?.id === mood.id
                      ? `0 4px 20px ${mood.color}30`
                      : "0 2px 10px rgba(45,106,79,0.04)",
                  }}
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: mood.color + "18" }}
                  >
                    <mood.Icon size={26} weight="duotone" style={{ color: mood.color }} />
                  </div>
                  <span className="text-xs font-medium text-[#1A2421]">{mood.label}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Step 2: Details */}
        {step === 2 && selectedMood && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            className="space-y-6"
          >
            {/* Selected Mood Display */}
            <div className="card-neuro p-6 flex items-center gap-4">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: selectedMood.color + "20" }}
              >
                <selectedMood.Icon size={30} weight="duotone" style={{ color: selectedMood.color }} />
              </div>
              <div>
                <p className="text-sm text-[#78857F]">You're feeling</p>
                <p className="font-['Outfit'] text-xl font-medium text-[#1A2421]" data-testid="selected-mood-label">
                  {selectedMood.label}
                </p>
              </div>
              <button
                onClick={() => setStep(1)}
                className="ml-auto text-xs text-[#2D6A4F] hover:underline"
                data-testid="change-mood-button"
              >
                Change
              </button>
            </div>

            {/* Intensity Slider */}
            <div className="card-neuro p-6">
              <label className="text-sm font-medium text-[#1A2421] mb-3 block">
                Intensity — <span className="text-[#2D6A4F] font-semibold">{intensity[0]}/10</span>
              </label>
              <Slider
                data-testid="mood-intensity-slider"
                value={intensity}
                onValueChange={setIntensity}
                max={10}
                min={1}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-[10px] text-[#78857F] mt-1.5">
                <span>Mild</span>
                <span>Intense</span>
              </div>
            </div>

            {/* Feelings Tags */}
            <div className="card-neuro p-6">
              <p className="text-sm font-medium text-[#1A2421] mb-3">What else are you feeling?</p>
              <div className="flex flex-wrap gap-2">
                {FEELINGS.map((f) => (
                  <button
                    key={f}
                    data-testid={`feeling-tag-${f.toLowerCase()}`}
                    onClick={() => toggleFeeling(f)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                      selectedFeelings.includes(f)
                        ? "bg-[#2D6A4F] text-white"
                        : "bg-[#EAE4D9] text-[#78857F] hover:bg-[#2D6A4F]/10 hover:text-[#2D6A4F]"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* Note */}
            <div className="card-neuro p-6">
              <p className="text-sm font-medium text-[#1A2421] mb-3">Anything else on your mind?</p>
              <Textarea
                data-testid="mood-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional — share what's going on..."
                className="border-[#E1D9CD] bg-[#FAF7F2] rounded-xl resize-none min-h-[80px] focus:border-[#2D6A4F]"
              />
            </div>

            {/* Submit */}
            <Button
              onClick={handleScan}
              disabled={loading}
              data-testid="mood-scan-submit"
              className="w-full rounded-full h-12 bg-[#2D6A4F] hover:bg-[#1B4936] text-white font-medium transition-all duration-300 hover:-translate-y-0.5 shadow-md"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Sparkle size={18} className="animate-spin" /> Mixing your formula...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Get My Mood Formula <ArrowRight size={18} />
                </span>
              )}
            </Button>
          </motion.div>
        )}

        {/* Step 3: Formula Result */}
        {step === 3 && formula && (
          <motion.div
            key="step3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <div className="card-neuro p-8 relative overflow-hidden" data-testid="formula-result-card">
              <div
                className="absolute top-0 right-0 w-32 h-32 rounded-full blur-[60px] opacity-20"
                style={{ backgroundColor: formula.color?.hex || "#84A98C" }}
              />
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#2D6A4F] mb-1">Your Mood Formula</p>
              <h2 className="font-['Outfit'] text-2xl font-medium text-[#1A2421] mb-6">
                {formula.name}
              </h2>

              {/* Breathwork */}
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <Wind size={20} weight="duotone" className="text-[#2D6A4F]" />
                  <span className="text-sm font-medium text-[#1A2421]">Breathwork</span>
                </div>
                <ol className="space-y-1 pl-7">
                  {formula.breathwork?.map((step, i) => (
                    <li key={i} className="text-sm text-[#78857F] list-decimal">{step}</li>
                  ))}
                </ol>
              </div>

              {/* Sound — with audio player */}
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <Wind size={20} weight="duotone" className="text-[#84A98C]" />
                  <span className="text-sm font-medium text-[#1A2421]">Sound Frequency</span>
                </div>
                <div className="pl-7">
                  <FrequencyPlayer
                    frequencyText={formula.sound_frequency}
                    accentColor="#84A98C"
                  />
                </div>
              </div>

              {/* Color */}
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <Palette size={20} weight="duotone" className="text-[#CDB4DB]" />
                  <span className="text-sm font-medium text-[#1A2421]">Color Visualization</span>
                </div>
                <div className="flex items-center gap-3 pl-7">
                  <div
                    className="w-10 h-10 rounded-xl border border-black/5"
                    style={{ backgroundColor: formula.color?.hex || "#84A98C" }}
                  />
                  <span className="text-sm text-[#78857F]">{formula.color?.name}</span>
                </div>
              </div>

              {/* Scent */}
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <Flower size={20} weight="duotone" className="text-[#F4A261]" />
                  <span className="text-sm font-medium text-[#1A2421]">Scent Pairing</span>
                </div>
                <p className="text-sm text-[#78857F] pl-7">{formula.scent}</p>
              </div>

              {/* Journaling */}
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <PencilLine size={20} weight="duotone" className="text-[#457B9D]" />
                  <span className="text-sm font-medium text-[#1A2421]">Journaling Prompt</span>
                </div>
                <p className="text-sm text-[#78857F] pl-7 italic">"{formula.journaling_prompt}"</p>
              </div>

              {/* What to Wear */}
              <div className="mb-5">
                <WhatToWear mood={selectedMood?.id} />
              </div>

              {/* Affirmation */}
              {formula.affirmation && (
                <div className="mt-6 p-4 rounded-2xl bg-[#2D6A4F]/5 border border-[#2D6A4F]/10">
                  <p className="text-center text-sm font-medium text-[#2D6A4F] italic">
                    "{formula.affirmation}"
                  </p>
                </div>
              )}
            </div>

            <Button
              onClick={resetScan}
              data-testid="scan-again-button"
              variant="outline"
              className="w-full rounded-full h-11 border-[#E1D9CD] hover:border-[#2D6A4F] text-[#1A2421]"
            >
              Scan Again
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
